/**
 * FASE E — CAMPO ÚNICO INTELIGENTE de documento (CPF **ou** RG).
 *
 * Um campo só, que se vira sozinho:
 *   * digita CPF  → máscara `000.000.000-00` e validação do dígito verificador;
 *   * digita RG   → máscara leve (aceita letra e o formato do estado);
 *   * em dúvida   → AVISA e oferece a troca ("é RG" / "é CPF") com um clique.
 *
 * Nada bloqueia: o documento é OPCIONAL e todo aviso é só aviso. O que o campo
 * entendeu aparece embaixo dele, em uma linha — é a "inteligência" visível.
 */
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { detectDocumento, smartMask, tipoDocumento, temLetra, textoAoEscolherTipo } from '../lib/documentos';

export default function CampoDocumentoInteligente({
  label = 'CPF ou RG',
  value,
  tipo = 'auto',
  onChange,
  onTipoChange,
  testId = 'documento',
  hint,
  className = '',
}) {
  const det = detectDocumento(value);
  const modo = tipoDocumento(value, tipo);
  const forcado = tipo === 'cpf' || tipo === 'rg';
  const vazio = det.tipo === null;

  // Trocar o tipo à mão JÁ reaplica a máscara: o que está na tela é o que salva.
  const trocarTipo = (novo) => {
    if (onTipoChange) onTipoChange(novo);
    if (onChange) onChange(textoAoEscolherTipo(value, novo));
  };

  // A linha de baixo: o que o CAMPO entendeu do que foi digitado.
  let linha;
  if (vazio) {
    linha = {
      cor: 'text-[#71717A]',
      icone: null,
      texto: hint || 'CPF (000.000.000-00) ou RG — pode digitar com ou sem pontuação.',
    };
  } else if (modo === 'rg') {
    linha = {
      cor: 'text-[#A1A1AA]',
      icone: null,
      texto: det.digitos > 11
        ? det.aviso
        : 'Tratado como RG — sem dígito verificador nacional (aceita letra).',
    };
    if (det.digitos > 11) linha.cor = 'text-amber-400';
  } else if (det.digitos === 11 && det.valido) {
    linha = { cor: 'text-emerald-400', icone: 'ok', texto: 'CPF válido — dígito verificador confere.' };
  } else if (det.digitos === 11) {
    linha = {
      cor: 'text-amber-400',
      icone: 'aviso',
      texto: 'Tem 11 dígitos, mas o CPF não confere — confira os números (ou é RG?).',
    };
  } else {
    linha = {
      cor: 'text-[#71717A]',
      icone: null,
      texto: 'Digitando como CPF — se o número for RG, clique em "é RG".',
    };
  }

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-[#A1A1AA] text-xs">{label}</Label>}

      <Input
        value={value || ''}
        // A máscara roda a cada tecla: CPF ganha pontuação; RG é organizado.
        onChange={(e) => onChange(smartMask(e.target.value, tipo))}
        inputMode={modo === 'rg' ? 'text' : 'numeric'}
        maxLength={modo === 'rg' ? 20 : 14}
        placeholder="000.000.000-00 ou RG (opcional)"
        data-testid={testId}
        aria-invalid={linha.icone === 'aviso' ? 'true' : 'false'}
        className={`bg-[#1A1A1A] border-[#27272A] text-white ${
          linha.icone === 'aviso' ? 'border-amber-500/60' : ''
        } ${className}`}
      />

      <div className="flex items-start justify-between gap-2">
        <p className={`text-micro leading-tight flex items-center gap-1 ${linha.cor}`} data-testid={`${testId}-tipo`}>
          {linha.icone === 'ok' && <CheckCircle2 className="w-3 h-3 shrink-0" />}
          {linha.icone === 'aviso' && <AlertTriangle className="w-3 h-3 shrink-0" />}
          {linha.texto}
        </p>

        {/* Saídas de 1 clique: quem sabe o que é o documento decide pelo campo. */}
        <div className="flex gap-2 shrink-0">
          {forcado ? (
            <button
              type="button"
              onClick={() => trocarTipo('auto')}
              data-testid={`${testId}-auto`}
              className="text-micro text-[#71717A] underline hover:text-white"
            >
              automático
            </button>
          ) : (
            !vazio && (
              <>
                {modo !== 'rg' && det.digitos > 0 && (
                  <button
                    type="button"
                    onClick={() => trocarTipo('rg')}
                    data-testid={`${testId}-e-rg`}
                    className="text-micro text-[#D4AF37] underline hover:text-white"
                  >
                    é RG
                  </button>
                )}
                {modo === 'rg' && !temLetra(value) && det.digitos > 0 && (
                  <button
                    type="button"
                    onClick={() => trocarTipo('cpf')}
                    data-testid={`${testId}-e-cpf`}
                    className="text-micro text-[#D4AF37] underline hover:text-white"
                  >
                    é CPF
                  </button>
                )}
              </>
            )
          )}
        </div>
      </div>

      {forcado && (
        <p className="text-micro text-[#71717A] leading-tight" data-testid={`${testId}-forcado`}>
          Marcado à mão como {forcado ? tipo.toUpperCase() : ''} — o campo não vai adivinhar.
        </p>
      )}
    </div>
  );
}
